import { useState, useCallback } from "react";
import { useUser } from "@/context/UserContext";
import type { Employee } from "@/types/employee";
import ProfileCard from "./components/ProfileCard";
import BasicInfoForm from "./components/BasicInfoForm";
import PasswordForm from "./components/PasswordForm";
import IdentityForm from "./components/IdentityForm";
import BankForm from "./components/BankForm";

const roleLabelMap: Record<string, string> = {
  admin: "超級管理員",
  manager: "主管",
  employee: "員工",
  trial: "試用用戶",
};

interface ToastState {
  message: string;
  type: "success" | "error";
}

export default function ProfilePage() {
  const { employee: contextEmployee, employeeId, userName, userEmail, userRole, isAdmin, isProfileComplete, refreshEmployee } = useUser();
  const [toast, setToast] = useState<ToastState | null>(null);

  const handleToast = useCallback((msg: string, type: "success" | "error" = "success") => {
    setToast({ message: msg, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const handleUpdate = useCallback((updated: Employee) => {
    refreshEmployee();
  }, [refreshEmployee]);

  const effectiveEmployee = contextEmployee;

  return (
    <div className="space-y-5 max-w-[1440px] mx-auto">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 px-4 py-2.5 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2 animate-in fade-in slide-in-from-top-2 ${
            toast.type === "error"
              ? "bg-accent-500 text-background-50"
              : "bg-foreground-800 text-background-50"
          }`}
        >
          <i
            className={`${toast.type === "error" ? "ri-error-warning-line" : "ri-check-line"} text-primary-400`}
          />
          {toast.message}
        </div>
      )}

      {/* Page Title */}
      <div>
        <h1
          className="text-xl font-bold text-foreground-900"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          個人中心
        </h1>
        <p className="text-sm text-foreground-500 mt-1">管理你的個人資料與身分驗證</p>
      </div>

      {/* Profile Incomplete Banner */}
      {!isAdmin && !isProfileComplete && (
        <div className="bg-accent-50 border border-accent-200 rounded-xl p-4 md:p-5 flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-alert-line text-accent-600 text-lg" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-bold text-accent-800">請先完成所有必填資料</h3>
            <p className="text-xs text-accent-600 mt-1">
              你必須完成以下三大區塊才能解鎖所有功能：① 基本資料（姓名、電話、地址、身分證字號）、② 身分驗證資料（身分證正反面）、③ 薪資帳戶資料（銀行資訊、帳號、銀行卡照片）。目前其他頁面已被暫時鎖定。
            </p>
          </div>
        </div>
      )}

      {/* Top Row: Profile + Basic Info */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-5">
        <div className="lg:col-span-1">
          <ProfileCard
            employeeId={employeeId}
            name={effectiveEmployee?.name || userName}
            email={effectiveEmployee?.email || userEmail}
            roleLabel={roleLabelMap[userRole] || userRole}
            avatarUrl={effectiveEmployee?.avatar_url || null}
            department={effectiveEmployee?.department}
            onAvatarChange={(url) => {
              if (!effectiveEmployee) return;
              handleUpdate({ ...effectiveEmployee, avatar_url: url });
            }}
            onToast={handleToast}
          />
        </div>
        <div className="lg:col-span-3">
          <BasicInfoForm
            employee={effectiveEmployee}
            onUpdate={handleUpdate}
            onToast={handleToast}
          />
        </div>
      </div>

      {/* Bottom Row: Password + Identity + Bank */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
        <PasswordForm onToast={handleToast} />
        <IdentityForm
          employee={effectiveEmployee}
          onUpdate={handleUpdate}
          onToast={handleToast}
        />
        <BankForm
          employee={effectiveEmployee}
          onUpdate={handleUpdate}
          onToast={handleToast}
        />
      </div>
    </div>
  );
}